import React, { useEffect, useState } from "react";
import DataTableBase from "../../Components/Common/DataTableBase/DataTableBase";
import { t } from "i18next";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  UncontrolledTooltip,
} from "reactstrap";
import AddOrder from "./Components/AddOrder";
import EditOrder from "./Components/EditOrder";
import { useLocation } from "react-router-dom";
import Flatpickr from "react-flatpickr";

import { useConfirmDialogStore } from "../../stores/Modal/ConfirmDialogStore";
import { useOrderStore } from "../../stores/Orders";
import moment from "moment";
import { useSiteStore } from "../../stores/Assets/site";
import { useKitchenStore } from "../../stores/Assets/kitchen";
import { canEdit } from "../../helpers/permissions";
import {
  DashedLine,
  DeliveryTime,
  DeliveryTimeChart,
  OrderStats,
} from "./Charts";
import {
  formatTime,
  processingTime,
} from "../../Components/Common/processingTime";

const formatItemType = (type) => {
  if (!type) return "Default";
  return type
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const formatItemOptions = (options) => {
  if (!Array.isArray(options) || options.length === 0) return "—";
  return options
    .map((option) => {
      const name = option?.option || option?.name;
      const choice = option?.choice || option?.value;
      if (name && choice) return `${name}: ${choice}`;
      return name || choice || "";
    })
    .filter(Boolean)
    .join(", ");
};

const Orders = () => {
  document.title = "Orders - Clickorder Admin";

  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const site_id = parseInt(queryParams.get("site_id")); // Get the "status" query param
  const date = queryParams.get("date"); // Get the "year" query param
  
  const [sort, setSort] = useState({
    column: "created_at",
    direction: "desc",
  });
  
  const [timezone, setTimezone] = useState(() => {
    // Capture user's timezone
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  });
  
  const [filters, setFilters] = useState({
    status: null,
    siteId: site_id ? site_id : NaN,
    kitchenId: NaN,
    from: date
      ? new Date(new Date(date).setDate(new Date(date).getDate() - 7))
      : (() => {
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);
          yesterday.setHours(0, 0, 0, 0);
          return yesterday;
        })(),
    to: (() => {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      return today;
    })(),
    sortColumn: "created_at",
    sortDirection: "desc",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  const [totalRows, setTotalRows] = useState(0);

  const [kitchenList, setKitchenList] = useState([]);
  const [siteList, setSiteList] = useState([]);
  const [orderList, setOrderList] = useState([]);

  // Loading states
  const [sitesLoading, setSitesLoading] = useState(true);
  const [kitchenLoading, setKitchenLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [initialDataLoading, setInitialDataLoading] = useState(true);

  const { getSites } = useSiteStore((state) => state);
  const { getKitchens, kitchens, getKitchensBySite } = useKitchenStore(
    (state) => state
  );
  const {
    getOrders,
    isLoading,
    orders,
    getOrderStats,
    orderStats,
    statsLoading,
  } = useOrderStore((state) => state);

  useEffect(() => {
    // Apply filters if query params are present, otherwise fetch default data
    if (site_id || date) {
      const loadInitialData = async () => {
        setInitialDataLoading(true);
        try {
          await Promise.all([getOrders(filters), getOrderStats(filters)]);
        } finally {
          setInitialDataLoading(false);
        }
      };
      loadInitialData();
    } else {
      setInitialDataLoading(false);
    }
  }, [site_id, date, getOrders]);

  const handleKitchenChange = (e) => {
    setFilters({ ...filters, kitchenId: parseInt(e.target.value) });
  };

  const handleSiteChange = (e) => {
    const siteId = parseInt(e.target.value);
    setFilters({ ...filters, siteId, kitchenId: NaN }); // Reset kitchen when site changes

    if (!e.target.value || isNaN(siteId)) {
      setKitchenList(kitchens);
      setKitchenLoading(false);
    }
    // Kitchen loading will be handled by the useEffect below
  };

  const handleStatusChange = (e) => {
    setFilters({ ...filters, status: e.target.value });
  };

  const applyFilters = async () => {
    setFilterLoading(true);
    try {
      await Promise.all([getOrders(filters), getOrderStats(filters)]);
    } finally {
      setFilterLoading(false);
    }
  };

  const resetFilters = async () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(0, 0, 0, 0);
    
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    
    const defaultFilters = {
      status: null,
      siteId: siteList.length > 0 ? siteList[0].site_id : NaN,
      kitchenId: NaN,
      from: yesterday,
      to: today,
      sortColumn: "created_at",
      sortDirection: "desc",
      timezone: timezone,
    };

    setSort({
      column: "created_at",
      direction: "desc",
    });
    setFilters(defaultFilters);

    setFilterLoading(true);
    try {
      await Promise.all([
        getOrders(defaultFilters),
        getOrderStats(defaultFilters),
      ]);
    } finally {
      setFilterLoading(false);
    }
  };

  const [hasAutoExecuted, setHasAutoExecuted] = useState(false);

  useEffect(() => {
    const fetchSites = async () => {
      setSitesLoading(true);
      try {
        const siteList = await getSites(); // Fetch sites
        setSiteList(siteList);
        
        // Auto-select first site if no site is selected from query params
        if (siteList && siteList.length > 0 && isNaN(filters.siteId)) {
          setFilters(prev => ({
            ...prev,
            siteId: siteList[0].site_id,
          }));
        }
      } catch (error) {
        console.error("Error fetching sites:", error);
      } finally {
        setSitesLoading(false);
      }
    };

    fetchSites(); // Call the fetch function

    const fetchKitchens = async () => {
      try {
        await getKitchens(); // Fetch all kitchens
      } catch (error) {
        console.error("Error fetching kitchens:", error);
      }
    };

    fetchKitchens();
  }, [getSites]);

  useEffect(() => {
    if (filters.siteId && !isNaN(filters.siteId)) {
      setKitchenLoading(true);
      setKitchenList([]); // Clear current list while loading
      const fetchKitchensBySite = async () => {
        try {
          await getKitchensBySite(filters.siteId, {
            page: 1,
            perPage: 1000,
          });
        } catch (error) {
          console.error("Error fetching kitchens by site:", error);
        } finally {
          setKitchenLoading(false);
        }
      };
      fetchKitchensBySite();
    } else {
      setKitchenList(kitchens);
      setKitchenLoading(false);
    }
  }, [filters.siteId, getKitchensBySite]);

  useEffect(() => {
    if (kitchens.length > 0) {
      setKitchenList(kitchens);
    }
  }, [kitchens]);

  // Auto-execute filters when first site is selected
  useEffect(() => {
    const autoExecuteFilters = async () => {
      if (!hasAutoExecuted && filters.siteId && !isNaN(filters.siteId) && !sitesLoading) {
        setHasAutoExecuted(true);
        await applyFilters();
      }
    };
    
    autoExecuteFilters();
  }, [filters.siteId, sitesLoading]);

  useEffect(() => {
    if (!orders) return;
    if (orders?.list?.length === 0) setOrderList([]);

    // Update orderList with the new structure
    setOrderList(orders.list || []);
    setTotalRows(orders.pagination?.totalItems || 0);
  }, [orders, orderStats]);

  const statusColor = (status) => {
    if (status === "PENDING") {
      return "warning";
    }
    if (status === "DELIVERED") {
      return "success";
    }
    if (status === "CANCELLED") {
      return "danger";
    }
    if (status === "IN_PROGRESS") {
      return "secondary";
    }
    if (status === "READY") {
      return "info";
    }
  };

  const statusOptions = [
    { value: "", label: "All" },
    { value: "PENDING", label: "Pending" },
    { value: "DELIVERED", label: "Delivered" },
    { value: "CANCELLED", label: "Canceled" },
    { value: "IN_PROGRESS", label: "In Progress" },
    { value: "READY", label: "Ready" },
  ];

  const columns = [
    {
      name: t("ID"),
      width: "80px",
      value: "order_number",
      selector: (row) => row?.order_id,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>{row?.order_number}</span>
        </div>
      ),
    },
    {
      name: t("Space"),
      selector: (row) => row?.space?.name,
      sortable: false,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>
            {row?.space?.name} (id: {row?.space?.id})
          </span>
        </div>
      ),
    },
    {
      name: t("Customer"),
      selector: (row) => row?.customer_name,
      sortable: false,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>{row?.customer_name}</span>
        </div>
      ),
    },
    {
      name: t("Item"),
      selector: (row) => row?.title,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>{row?.title}</span>
        </div>
      ),
    },
    {
      name: t("Type"),
      selector: (row) => row?.type,
      sortable: false,
      wrap: true,
      width: "110px",
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>{formatItemType(row?.type)}</span>
        </div>
      ),
    },
    {
      name: t("Options"),
      selector: (row) => formatItemOptions(row?.options),
      sortable: false,
      wrap: true,
      grow: 2,
      cell: (row) => (
        <div>{formatItemOptions(row?.options)}</div>
      ),
    },
    {
      name: t("Status"),
      selector: (row) => row?.status,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div
          style={{ fontSize: "14px" }}
          className={`badge   bg-soft-${statusColor(
            row?.status
          )} text-${statusColor(row?.status)}`}
        >
          <span>{row?.status}</span>
        </div>
      ),
    },
    {
      name: t("Source"),
      width: "100px",
      selector: (row) => row?.user_id,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div
          style={{ fontSize: "14px" }}
          className={`badge cursor-normal bg-soft-${
            row.source ? "primary" : row.user_id ? "success" : "danger"
          } text-${row.source ? "primary" : row.user_id ? "success" : "secondary"}`}
        >
          {row.source ? row.source : row.user_id ? "Mobile" : "QR"}
        </div>
      ),
    },
    {
      name: t("Time"),
      selector: (row) => row?.created_at,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div>{moment(row?.created_at).format("DD/MM/YYYY hh:mm")}</div>
      ),
    },
    {
      name: t("Processing Time"),
      selector: (row) => row?.delivery_time_s,
      sortable: true,
      wrap: true,
      cell: (row) => <div>{formatTime(row?.delivery_time_s)}</div>,
    },
  ];

  const searchHandler = (searchText) => {
    // getOrders({
    //   search: searchText,
    // });
  };

  const [showAddOrderModal, setShowAddOrderModal] = useState(false);
  const [showEditOrderModal, setShowEditOrderModal] = useState(false);
  const toggleAddOrderModal = () => {
    setShowAddOrderModal(!showAddOrderModal);
  };

  const [selectedRow, setSelectedRow] = useState(null);
  const toggleEditOrderModal = (row) => {
    setSelectedRow(row);
    setShowEditOrderModal(!showEditOrderModal);
  };

  const onChangePage = (page) => {
    getOrders({
      ...filters,
      page: page,
      sortColumn: sort.column,
      sortDirection: sort.direction,
    });
  };

  const [canEditOrder, setCanEditOrder] = useState(false);

  useEffect(() => {
    const checkPermission = async () => {
      const res = await canEdit("order");
      setCanEditOrder(res);
    };

    checkPermission();
  }, []);

  const handleSortChange = (column, direction) => {
    setSort({ column, direction });
    getOrders({
      ...filters,
      sortColumn: column,
      sortDirection: direction,
      page: 1, // Reset to first page when sorting changes
    });
  };

  // Show initial loading spinner while data is being fetched
  if (initialDataLoading || (sitesLoading && siteList.length === 0)) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "400px" }}
      >
        <div className="text-center">
          <Spinner size="lg" />
          <div className="mt-2">Loading orders...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <DataTableBase
        tableTitle={"ORDERS"}
        data={orderList}
        columns={columns}
        loading={isLoading || filterLoading}
        paginationTotalRows={totalRows}
        onChangePage={onChangePage}
        onSearchIconClick={searchHandler}
        showActionColumn={false}
        showSubHeader={false}
        showActionButtons={false}
        currentSortColumn={sort.column}
        currentSortDirection={sort.direction}
        onSortChange={handleSortChange}
      >
        <div className="d-flex flex-column gap-3 w-100">
          {/* First Row - Status, Site, Station */}
          <div className="row g-3 align-items-center">
            {statusOptions.length > 0 && (
              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label fw-semibold mb-1">Status</label>
                <select
                  className="form-control"
                  value={filters.status || ""}
                  onChange={handleStatusChange}
                  disabled={filterLoading}
                >
                  {statusOptions.map((status, indx) => (
                    <option key={indx} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="col-12 col-md-6 col-lg-3">
              <label className="form-label fw-semibold mb-1">Site</label>
              <div className="position-relative">
                <select
                  className="form-control"
                  value={filters.siteId || ""}
                  onChange={handleSiteChange}
                  disabled={sitesLoading || filterLoading}
                >
                  <option value="">
                    {sitesLoading ? "Loading sites..." : "All"}
                  </option>
                  {siteList.map((site, indx) => (
                    <option key={indx} value={site.site_id}>
                      {site.site_id} - {site.name}
                    </option>
                  ))}
                </select>
                {sitesLoading && (
                  <div
                    className="position-absolute"
                    style={{
                      right: "30px",
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  >
                    <Spinner size="sm" />
                  </div>
                )}
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <label className="form-label fw-semibold mb-1">Station</label>
              <div className="position-relative">
                <select
                  className="form-control"
                  value={filters.kitchenId || ""}
                  onChange={handleKitchenChange}
                  disabled={kitchenLoading || filterLoading}
                >
                  <option value="">
                    {kitchenLoading ? "Loading kitchens..." : "All"}
                  </option>
                  {kitchenList.map((kitchen, indx) => (
                    <option key={indx} value={kitchen.kitchen_id}>
                      {kitchen.kitchen_id} - {kitchen.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <label className="form-label fw-semibold mb-1">Date Range</label>
              <Flatpickr
                options={{
                  mode: "range",
                  enableTime: true,
                  dateFormat: "Y-m-d H:i",
                  defaultDate: [filters.from, filters.to],
                }}
                className="form-control bg-white"
                value={[filters.from, filters.to]}
                onChange={(date) => {
                  if (date && date.length === 2) {
                    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                    setTimezone(userTimezone);
                    setFilters({
                      ...filters,
                      from: date[0],
                      to: date[1],
                      timezone: userTimezone,
                    });
                  }
                }}
                disabled={filterLoading}
              />
         
            </div>
          </div>

          {/* Second Row - Action Buttons */}
          <div className="d-flex align-items-center justify-content-start gap-2">
            <Button
              style={{ background: "#ce9558" }}
              onClick={applyFilters}
              disabled={filterLoading || sitesLoading}
              className="d-flex align-items-center gap-2"
            >
              {filterLoading ? (
                <>
                  <Spinner size="sm" />
                  <span>Filtering...</span>
                </>
              ) : (
                <>
                  <i className="ri-filter-line"></i>
                  <span>Filter</span>
                </>
              )}
            </Button>
            <Button
              color="danger"
              onClick={resetFilters}
              disabled={filterLoading || sitesLoading}
              className="d-flex align-items-center gap-2"
            >
              {filterLoading ? (
                <>
                  <Spinner size="sm" />
                  <span>Resetting...</span>
                </>
              ) : (
                <>
                  <i className="ri-refresh-line"></i>
                  <span>Reset</span>
                </>
              )}
            </Button>
          </div>
        </div>

        {statsLoading || filterLoading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "200px" }}
          >
            <div className="text-center">
              <Spinner size="lg" />
              <div className="mt-2">Loading statistics...</div>
            </div>
          </div>
        ) : (
          <div
            style={{ width: "100%" }}
            className="d-flex justify-content-center align-items-center "
          >
            <DeliveryTime
              dataColors={'["#ce9558","--vz-primary", "--vz-danger"]'}
              deliveryTimes={orderStats?.deliveryTimes}
              canceledTimes={orderStats?.canceledTimes}
            />
          </div>
        )}
      </DataTableBase>

      {showAddOrderModal && (
        <AddOrder
          toggleAddOrderModal={toggleAddOrderModal}
          showAddOrderModal={showAddOrderModal}
        />
      )}

      {selectedRow && (
        <EditOrder
          toggleEditOrderModal={toggleEditOrderModal}
          showEditOrderModal={showEditOrderModal}
          rowData={selectedRow}
        />
      )}
    </>
  );
};

export default Orders;
